import {Component, OnInit} from '@angular/core';
import {AuthenticationService} from "../../../_auth/authentication.service";
import {FormsModule} from "@angular/forms";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {UserAuthenticators} from "../../../models/admin_owner/user/external-identity-info";
import {AccountLinkOptionsComponent} from "../../../_auth/linking/account-link-options.component";
import {describeAccountActionError} from "../../../_shared/errors/account-action-errors";

@Component({
  selector: 'app-my-profile',
  templateUrl: './my-profile.component.html',
  // CommonModule is deliberately absent: the template moved to the built-in control flow, and [ngModel] is
  // the only directive left on it.
  imports: [
    FormsModule,
    AccountLinkOptionsComponent
  ],
  styleUrls: ['./my-profile.component.scss']
})
export class MyProfileComponent implements OnInit {
  /** The current employee's password-login (CanLogin) status + linked external authenticators. */
  authenticators?: UserAuthenticators;

  constructor(protected authenticationService: AuthenticationService, private gas: GlobalAlertService) {
  }

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.authenticationService.getEmployeeAuthenticators().subscribe({
      next: (a) => (this.authenticators = a),
      error: (err) => console.error('Failed to load employee authenticators', err)
    });
  }

  /**
   * The e-mail + password door finished, here, with no navigation.
   *
   * The list is refreshed because it is the thing on screen that would otherwise still read "Keine externe
   * Authentifizierung verknüpft" beside the account that was just created.
   */
  onNativeClaimed() {
    this.gas.createAndShow("Erfolg", "Konto verknüpft.", AlertLevel.success);
    this.load();
  }

  /**
   * A provider door would not start.
   *
   * Deliberately NOT routed through {@link showError}: that maps the account-safety refusals this panel's own
   * endpoints answer with, and none of them can come out of a redirect that never left. There is nothing here
   * for the employee to correct, so the sentence says what happened and stops.
   */
  onLinkFailed() {
    this.gas.createAndShow("Fehler", "Die Verknüpfung konnte nicht gestartet werden.", AlertLevel.error);
  }

  removeAuthenticator(identityId: string) {
    if (!confirm("Diese externe Authentifizierung entfernen?")) return;
    this.authenticationService.removeEmployeeAuthenticator(identityId).subscribe({
      next: () => {
        this.gas.createAndShow("Erfolg", "Externe Authentifizierung entfernt.", AlertLevel.success);
        this.load();
      },
      error: (err) => this.showError(err)
    });
  }

  togglePasswordLogin(enabled: boolean) {
    this.authenticationService.setEmployeePasswordLogin(enabled).subscribe({
      next: () => this.load(),
      error: (err) => {
        this.showError(err);
        this.load(); // revert toggle to server state
      }
    });
  }

  /**
   * Reports a failed action, in **German mapped from the error code**.
   *
   * It used to render `err.error.description` — the server's `ErrorCatalog` text, English by design and
   * written for a log rather than for a person (frontend style guide, *"Surfacing an API error"*). The two
   * refusals an employee actually meets on this panel are the account-safety rules, and they are rules
   * somebody has to act on: the worst kind of sentence to leave in the wrong language.
   *
   * The mapping is shared with the two administrator-facing screens that enforce the same guard, so an
   * employee and the administrator looking at their account read the same rule — see
   * {@link describeAccountActionError}.
   */
  private showError(err: any) {
    this.gas.createAndShow("Fehler",
      describeAccountActionError(err, "Die Aktion konnte nicht ausgeführt werden."), AlertLevel.error);
  }
}
